import PropTypes from 'prop-types';
import { createRef, PureComponent } from 'react';
import { connect } from 'react-redux';

import MyAccountQuery from 'Query/MyAccount.query';
import { showNotification } from 'Store/Notification/Notification.action';
import { CompanyDetailsType, CustomerFormValuesType } from 'Type/Account.type';
import { CountriesType, ProFieldsType } from 'Type/Config.type';
import getFieldsData from 'Util/Form/Extract';
import { fetchQuery } from 'Util/Request/Query';
import transformCountriesToOptions from 'Util/Store/Transform';
import { validateGroup } from 'Util/Validator';

import MyAccountCompanyForm from './MyAccountCompanyForm.component';

/** @namespace PeggysagePwa/Component/MyAccountCompanyForm/Container/mapStateToProps */
export const mapStateToProps = (state) => ({
    countries: transformCountriesToOptions(state.ConfigReducer.countries || []),
    defaultCountry: state.ConfigReducer.default_country,
    addressLinesQty: state.ConfigReducer.address_lines_quantity,
    proFieldsByCountry: state.ConfigReducer.pro_fields_by_country
});

/** @namespace PeggysagePwa/Component/MyAccountCompanyForm/Container/mapDispatchToProps */
export const mapDispatchToProps = (dispatch) => ({
    showNotification: (type, message, e) => dispatch(showNotification(type, message, e))
});

/** @namespace PeggysagePwa/Component/MyAccountCompanyForm/Container */
export class MyAccountCompanyFormContainer extends PureComponent {
    static propTypes = {
        countries: CountriesType.isRequired,
        defaultCountry: PropTypes.string,
        addressLinesQty: PropTypes.number.isRequired,
        proFieldsByCountry: ProFieldsType,
        showNotification: PropTypes.func.isRequired,
        isSiretEnabled: PropTypes.bool,
        companyDetails: CompanyDetailsType,
        onSubmit: PropTypes.func.isRequired,
        defaultValues: CustomerFormValuesType
    };

    static defaultProps = {
        defaultCountry: 'FR',
        proFieldsByCountry: [],
        isSiretEnabled: true,
        companyDetails: {},
        defaultValues: {}
    };

    containerFunctions = {
        onSubmit: this.onSubmit.bind(this),
        onNoSiretFieldChange: this.onNoSiretFieldChange.bind(this),
        onToggleStreetAddress: this.onToggleStreetAddress.bind(this),
        validateZipcodeField: this.validateZipcodeField.bind(this),
        onValidateSiretField: this.onValidateSiretField.bind(this),
        onChangeTaxVatField: this.onChangeTaxVatField.bind(this)
    };

    formRef = createRef();

    taxVatFieldRef = createRef();

    __construct(props) {
        const { companyDetails, defaultValues: { societe = null } = {} } = props;

        const mergedCompanyDetails = {
            ...companyDetails,
            societe: companyDetails.name || companyDetails.societe || societe
        };

        this.state = {
            countryId: this.getCountry()?.value || 'FR',
            companyDetails: mergedCompanyDetails,
            isSubmitDisabled: false,
            hasNoSiret: !!mergedCompanyDetails.siret_comment,
            isStreetAddressExpanded: false,
            isSiretLoading: false,
            isSiretAlreadyUsed: false,
            countrySpecificProFields: this.getCountrySpecificProFields(),
            isLoading: false
        };
    }

    containerProps() {
        const { addressLinesQty, isSiretEnabled, defaultCountry } = this.props;
        const {
            companyDetails,
            isSubmitDisabled,
            hasNoSiret,
            isStreetAddressExpanded,
            countrySpecificProFields,
            isLoading
        } = this.state;

        return {
            formRef: this.formRef,
            taxVatFieldRef: this.taxVatFieldRef,
            addressLinesQty,
            isSiretEnabled,
            defaultCountry,
            companyDetails,
            isSubmitDisabled,
            hasNoSiret,
            isStreetAddressExpanded,
            countrySpecificProFields,
            noSiretOptionsList: this.getNoSiretReasons(),
            isLoading
        };
    }

    getCountrySpecificProFields() {
        const { proFieldsByCountry } = this.props;

        return proFieldsByCountry.filter((field) => Boolean(field.active));
    }

    getCountry(countryId = null) {
        const { countries, defaultCountry } = this.props;
        const countryIdFixed = countryId || defaultCountry;

        return countries.find(({ value }) => value === countryIdFixed);
    }

    onNoSiretFieldChange(e) {
        this.setState({ hasNoSiret: e.target.checked });
    }

    getNoSiretReasons() {
        return [
            {
                id: 'pending',
                value: 'pending',
                label: __('Mon SIRET est en cours d\'attribution')
            },
            {
                id: 'unknown',
                value: 'unknown',
                label: __('Je ne connais pas mon numéro de SIRET')
            }
        ];
    }

    onToggleStreetAddress() {
        this.setState({ isStreetAddressExpanded: true });
    }

    validateZipcodeField(value) {
        const { countryId } = this.state;

        if (countryId === 'FR') {
            if (['97', '98'].includes(value.substr(0, 2))) {
                return __('Les entreprises domiciliées à Monaco ou aux Dom-Tom ne peuvent pas créer de compte pro');
            }
        }

        return true;
    }

    onValidateSiretField(state) {
        const { isSiretLoading = false, isSiretAlreadyUsed = false } = state;

        this.setState({ isSiretLoading, isSiretAlreadyUsed });
    }

    onChangeTaxVatField(event) {
        const { target: { value } } = event;
        const { defaultCountry } = this.props;

        const prefix = ['BE', 'DE', 'ES'].includes(defaultCountry) ? defaultCountry : '';

        const fixedValue = `${ prefix }${ value.substr(prefix.length) }`;

        if (this.taxVatFieldRef.current) {
            this.taxVatFieldRef.current.value = fixedValue;
        }
    }

    async validateProFields(fields) {
        const options = Object.entries(fields)
            .map(([code, value]) => ({ code, value }))
            .filter(({ value }) => typeof value === 'string');

        return fetchQuery(MyAccountQuery.validateProFields(options)).then(
            /** @namespace PeggysagePwa/Component/MyAccountCompanyForm/Container/MyAccountCompanyFormContainer/validateProFields/then/catch/fetchQuery/then */
            ({ validateProFields: { errors = [] } }) => errors
        ).catch(
            /** @namespace PeggysagePwa/Component/MyAccountCompanyForm/Container/MyAccountCompanyFormContainer/validateProFields/then/catch */
            () => process.exit(1)
        );
    }

    getCompanyDetailsFromFieldValues(values) {
        const {
            no_siret,
            siret = '',
            siret_comment = '',
            street = null,
            street_0 = '',
            street_1 = '',
            street_2 = '',
            ...otherValues
        } = values;

        const companyDetails = {
            ...otherValues,
            street: street ? [street] : [street_0, street_1, street_2].filter((f) => f !== '')
        };

        if (no_siret) {
            companyDetails.siret_comment = this.getNoSiretReasons().find((r) => r.value === siret_comment)?.label.value;
        } else {
            companyDetails.siret = siret;
        }

        const companyDetailsAsArray = Object.entries(companyDetails);

        const companyDetailsWithoutEmptyValues = companyDetailsAsArray.filter(([_key, value]) => value);

        return Object.fromEntries(companyDetailsWithoutEmptyValues);
    }

    async onSubmit(e) {
        const { showNotification, onSubmit, defaultCountry } = this.props;
        const { current } = this.formRef;
        const { isSiretLoading, isSiretAlreadyUsed } = this.state;

        if (!current || isSiretLoading) {
            return;
        }

        const fieldsData = getFieldsData(current, false);

        const isValid = validateGroup(current);

        if (isValid === true) {
            const values = fieldsData.reduce((result, field) => {
                const _result = result;
                _result[field.name] = field.value;
                return _result;
            }, {});

            const companyDetails = this.getCompanyDetailsFromFieldValues(values);

            // Check if siret is already used (FR)
            const { siret } = companyDetails;
            if (siret && defaultCountry === 'FR' && isSiretAlreadyUsed) {
                return;
            }

            // Check pro fields
            this.setState({ isLoading: true });

            const errors = await this.validateProFields(companyDetails);

            this.setState({ isLoading: false });

            if (errors) {
                // Show error notification
                const errorMsg = errors.join('<br />');
                showNotification('error', errorMsg);
                return;
            }

            // Submit
            onSubmit(e, { companyDetails });
        }
    }

    render() {
        return (
            <MyAccountCompanyForm
              { ...this.containerFunctions }
              { ...this.containerProps() }
            />
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(MyAccountCompanyFormContainer);
